import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  useTheme,
  Avatar,
  alpha,
  useMediaQuery,
  Alert,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TagIcon from "@mui/icons-material/Tag";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TeamMessageList from "../components/TeamMessageList";
import TeamChannelMemberStrip from "../components/TeamChannelMemberStrip";
import TeamMessageInput from "../components/TeamMessageInput";
import TeamTypingIndicator from "../components/TeamTypingIndicator";
import TeamSpaceSettingsDialog from "../components/TeamSpaceSettingsDialog";
import TeamSpacePaneTabs from "../components/TeamSpacePaneTabs";
import TeamSpacePaneChrome from "../components/TeamSpacePaneChrome";
import { getInitials } from "../../../utils/utils";
import TeamPresenceAvatar from "../../../components/TeamPresenceAvatar";
import FileDropHandler from "../../../components/MessageHistory/FileDropHandler";
import { useTeamPresenceOptional } from "../../../context/TeamPresenceContext";
import TeamForwardDialog from "../components/TeamForwardDialog";
import { fetchTeamBoardCardLocation, markTeamMessagePlayed, toggleTeamMessageReaction } from "../../../api/client";
import { buildForwardSendPayload } from "../utils/teamMessageUtils";
import { emptyTeamMentions, isEntityDiscussionSpace } from "../utils/teamMentions";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import {
  resolveTeamSpaceLeadId,
  resolveTeamSpaceCardId,
  TEAM_MOBILE_MEDIA_QUERY,
  isMasterTeamDmReadOnly,
  teamMobileThreadHeaderRowSx,
  teamMobileThreadHeaderTitleSx,
} from "../utils/teamUiUtils";
import { useAuthContext } from "../../../context/AuthContext";
import { useRealtimeTeamTyping } from "../../../hooks/useRealtimeTeamTyping";
import { openCallPopupWindow } from "../../../utils/openCallPopupWindow";
import { openTeamPopupWindow } from "../../../utils/openTeamPopupWindow";
import { openTeamTasksPopupWindow } from "../../../utils/openTeamTasksPopupWindow";
import OpenInPopupIconButton from "../../../components/common/OpenInPopupIconButton";
import BoardPage from "../../TeamTasks/BoardPage";
import { formatTaskSpaceName } from "../../TeamTasks/utils/taskDisplayName";
import { TeamTasksFeedbackProvider } from "../../TeamTasks/components/TeamTasksFeedbackProvider";
import AiGenerateTasksDialog from "../../../components/ConversationDetails/AiGenerateTasksDialog";
import { useActiveVideoRooms, invalidateActiveVideoRooms } from "../../../hooks/useActiveVideoRooms";
import { ActiveVideoJoinControl } from "../../../components/ActiveVideo/ActiveVideoIndicator";
import { openAgentVideoJoinUrl } from "../../../utils/activeVideoUi";
import { useQueryClient } from "react-query";

const TeamMessagePane = ({
  containerStyle,
  isMobile,
  space,
  members,
  messages,
  currentUserId,
  onSend,
  onDeleteMessage,
  isSending,
  isLoading,
  loadError = false,
  hasMore,
  onLoadMore,
  leadId,
  onBack,
  canManageChannel,
  onUpdateSpace,
  onAddMember,
  onRemoveMember,
  onDeleteSpace,
  isAdminBusy,
  spaces = [],
  onForwardMessage,
  onSyncRecentMessages,
  teamCompany = null,
  standalone = false,
  onNavigateToQuotedSpace,
  portalTeamMode = false,
}) => {
  const { t } = useTranslation("team");
  const { t: tTasks } = useTranslation("teamTasks");
  const { t: tLive } = useTranslation("settings");
  const { userData } = useAuthContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const isCompactLayout = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const presenceCtx = useTeamPresenceOptional();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aiTasksOpen, setAiTasksOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardBusy, setForwardBusy] = useState(false);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [activePane, setActivePane] = useState("messages");
  const [videoPopupBlocked, setVideoPopupBlocked] = useState(false);
  const composerRef = useRef(null);
  const queryClient = useQueryClient();
  const { byTeamSpaceId } = useActiveVideoRooms();
  const activeVideoRoom = space?.id ? byTeamSpaceId.get(Number(space.id)) : null;

  const handleJoinActiveVideo = useCallback(
    (room) => {
      const joinUrl = room?.agentJoinUrl || room?.joinUrl;
      if (!joinUrl) return;
      openAgentVideoJoinUrl(joinUrl, {
        context: "team",
        onBlocked: () => setVideoPopupBlocked(true),
      });
      invalidateActiveVideoRooms(queryClient);
    },
    [queryClient]
  );

  const { typingUsers } = useRealtimeTeamTyping({
    spaceId: space?.id,
    currentUserId,
  });

  const handleReply = useCallback((msg) => {
    setEditingMessage(null);
    setReplyTo(msg);
    setForwardOpen(false);
  }, []);

  const handleEdit = useCallback((msg) => {
    setReplyTo(null);
    setForwardOpen(false);
    setEditingMessage(msg);
  }, []);

  const handleDelete = useCallback(
    async (msg) => {
      if (!msg?.id || !onDeleteMessage) return;
      if (!window.confirm(t("deleteMessageConfirm"))) return;
      try {
        await onDeleteMessage(msg.id);
        if (editingMessage?.id === msg.id) setEditingMessage(null);
      } catch (err) {
        console.error("[TeamMessagePane] delete:", err);
      }
    },
    [onDeleteMessage, editingMessage?.id, t]
  );

  const handleForwardOpen = useCallback((msg) => {
    setForwardMessage(msg);
    setForwardOpen(true);
  }, []);

  const handleReact = useCallback(
    async (msg, emoji) => {
      if (!msg?.id) return;
      const mine = (msg.reactions || []).find(
        (r) => String(r.userId) === String(currentUserId) && r.emoji === emoji
      );
      try {
        await toggleTeamMessageReaction(msg.id, mine ? "" : emoji, teamCompany);
        await onSyncRecentMessages?.();
      } catch (err) {
        console.error("[TeamMessagePane] reaction:", err);
      }
    },
    [currentUserId, onSyncRecentMessages, teamCompany]
  );

  const handleAudioPlayed = useCallback(
    async (msg) => {
      if (!msg?.id || msg?.messageType !== "audio") return;
      if (msg.authorType === "user" && String(msg.authorId) === String(currentUserId)) return;
      try {
        await markTeamMessagePlayed(msg.id, teamCompany);
        await onSyncRecentMessages?.();
      } catch (err) {
        console.error("[TeamMessagePane] audio played:", err);
      }
    },
    [currentUserId, onSyncRecentMessages, teamCompany]
  );

  const handleForwardSubmit = useCallback(
    async ({ targetSpaceId, caption }) => {
      if (!forwardMessage || !onForwardMessage) return;
      setForwardBusy(true);
      try {
        const payload = buildForwardSendPayload(forwardMessage, { caption });
        await onForwardMessage({
          spaceId: targetSpaceId,
          ...payload,
          mentions: emptyTeamMentions(),
        });
      } finally {
        setForwardBusy(false);
      }
    },
    [forwardMessage, onForwardMessage]
  );

  const isDm = space?.type === "dm";
  const dmReadOnly = !portalTeamMode && isMasterTeamDmReadOnly(userData, space);
  const isAgentDm = isDm && space?.dmPeerType === "agent";
  const isChatbotDm = isDm && space?.dmPeerType === "chatbot";
  const isHumanDm = isDm && !isAgentDm && !isChatbotDm;
  const dmPeerId = isHumanDm ? space?.dmPeerUserId : null;
  const dmPeerAvatarUrl = useMemo(() => {
    if (!dmPeerId) return null;
    if (space?.dmPeerAvatarUrl) return space.dmPeerAvatarUrl;
    const peer = (members || []).find(
      (m) => m.participantType === "user" && String(m.participantId) === String(dmPeerId)
    );
    return peer?.avatarUrl || null;
  }, [dmPeerId, space?.dmPeerAvatarUrl, members]);

  const resolvedCardId = useMemo(
    () => (space ? resolveTeamSpaceCardId(space) : null),
    [space]
  );

  const resolvedLeadId = useMemo(() => {
    if (resolvedCardId) return null;
    return space ? leadId ?? resolveTeamSpaceLeadId(space, messages) : leadId ?? null;
  }, [leadId, space, messages, resolvedCardId]);

  const handleOpenRequest = useCallback(() => {
    if (!resolvedLeadId) return;
    openCallPopupWindow(resolvedLeadId, { navigate });
  }, [resolvedLeadId, navigate]);

  const handleOpenTask = useCallback(
    async (cardIdOverride) => {
      const cardId =
        cardIdOverride != null && Number(cardIdOverride) > 0
          ? Number(cardIdOverride)
          : resolvedCardId;
      if (!cardId) return;
      try {
        let boardId = space?.boardId != null ? Number(space.boardId) : null;
        if (!Number.isFinite(boardId) || boardId <= 0) {
          const loc = await fetchTeamBoardCardLocation(cardId);
          boardId = loc?.boardId != null ? Number(loc.boardId) : null;
        }
        if (!Number.isFinite(boardId) || boardId <= 0) {
          console.warn("[TeamMessagePane] open task: board not found for card", cardId);
          return;
        }
        openTeamTasksPopupWindow({ boardId, cardId });
      } catch (err) {
        console.error("[TeamMessagePane] open task:", err);
      }
    },
    [resolvedCardId, space?.boardId]
  );

  if (!space) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          alignSelf: "stretch",
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: onBack ? "flex-start" : "center",
          textAlign: "center",
          px: onBack ? 0 : 3,
          overflow: "hidden",
          ...containerStyle,
        }}
      >
        {onBack ? (
          <Box
            sx={{
              px: { xs: 0.5, sm: 1 },
              py: { xs: 0.75, sm: 1 },
              borderBottom: 1,
              borderColor: alpha(theme.palette.divider, 0.55),
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            <IconButton onClick={onBack} aria-label={t("back")} edge="start">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1, textAlign: "left" }}>
              {t("title")}
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              mb: 2,
            }}
          >
            <ForumOutlinedIcon sx={{ fontSize: 36, color: "primary.main" }} />
          </Box>
          <Typography variant="h6" color="text.primary" fontWeight={600} gutterBottom>
            {t("noSpaceSelectedTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
            {t("noSpaceSelected")}
          </Typography>
        </Box>
      </Box>
    );
  }

  const dmPresence = dmPeerId && presenceCtx ? presenceCtx.getPresence(dmPeerId) : null;
  const dmPresenceLabel =
    dmPresence?.kind === "online"
      ? tLive("dashboardPage.live.onlineNow")
      : dmPresence?.kind === "away"
        ? tLive("dashboardPage.live.away")
        : dmPresence?.kind === "offline"
          ? tLive("dashboardPage.live.presenceOffline")
          : null;
  const taskChannelName = !isDm ? formatTaskSpaceName(space) : null;
  const title = isDm
    ? space.dmPeerName || t("directMessages")
    : taskChannelName || space.name || (space.slug ? space.slug : `Canal ${space.id}`);
  const displayTitle = isDm ? title : taskChannelName || `#${title.replace(/^#/, "")}`;

  const humanMembers = (members || []).filter((m) => m.participantType === "user");
  const memberChips =
    !isDm && members?.length
      ? members.filter(
          (m) =>
            m.participantType !== "user" ||
            String(m.participantId) !== String(currentUserId)
        )
      : [];

  const subtitleText = isDm
    ? teamCompany && space.workspaceLabel
      ? space.workspaceLabel
      : isAgentDm
        ? t("agentDmSubtitle")
        : isChatbotDm
          ? t("chatbotDmSubtitle")
          : dmPresenceLabel || t("dmSubtitle")
    : space.description || t("channelMemberCount", { count: humanMembers.length || 1 });

  const showSubtitle = Boolean(subtitleText) && !isCompactLayout;
  const isMobileThreadHeader = isCompactLayout && Boolean(onBack);
  const showChannelTabs = !isDm && space?.type === "channel";
  const canSuggestAiTasks = Boolean(space?.id);
  const headerMenuOpen = Boolean(headerMenuAnchor);

  const headerActionMenu = (
    <Menu
      anchorEl={headerMenuAnchor}
      open={headerMenuOpen}
      onClose={() => setHeaderMenuAnchor(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {resolvedCardId ? (
        <MenuItem
          onClick={() => {
            handleOpenTask();
            setHeaderMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <AssignmentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("openTask")}
        </MenuItem>
      ) : null}
      {resolvedLeadId ? (
        <MenuItem
          onClick={() => {
            handleOpenRequest();
            setHeaderMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <ChatOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("openRequest")}
        </MenuItem>
      ) : null}
      {canSuggestAiTasks ? (
        <MenuItem
          onClick={() => {
            setAiTasksOpen(true);
            setHeaderMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <AutoAwesomeIcon fontSize="small" color="primary" />
          </ListItemIcon>
          {tTasks("aiTasksButtonLabel")}
        </MenuItem>
      ) : null}
      <MenuItem
        onClick={() => {
          setSettingsOpen(true);
          setHeaderMenuAnchor(null);
        }}
      >
        <ListItemIcon>
          <SettingsOutlinedIcon fontSize="small" />
        </ListItemIcon>
        {t("spaceSettings")}
      </MenuItem>
    </Menu>
  );

  const aiSuggestButton = canSuggestAiTasks ? (
    <Tooltip title={tTasks("aiTasksButtonHint")}>
      <IconButton
        size="small"
        onClick={() => setAiTasksOpen(true)}
        aria-label={tTasks("aiTasksButtonLabel")}
        sx={{
          border: 1,
          borderColor: alpha(theme.palette.primary.main, 0.3),
          borderRadius: 1.5,
          color: "primary.main",
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        alignSelf: "stretch",
        bgcolor: showChannelTabs ? "background.default" : theme.palette.background.paper,
        overflow: "hidden",
        ...containerStyle,
      }}
    >
      {showChannelTabs ? (
        <TeamSpacePaneChrome>
          {isMobileThreadHeader ? (
            <Box sx={{ flexShrink: 0 }}>
              <Box
                sx={{
                  ...teamMobileThreadHeaderRowSx,
                  gap: 0.25,
                  px: 0.75,
                }}
              >
                <IconButton
                  onClick={onBack}
                  aria-label={t("back")}
                  edge="start"
                  size="medium"
                  sx={{ flexShrink: 0, ml: -0.5 }}
                >
                  <ArrowBackIcon />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", pr: 0.5, gap: 0.5 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={teamMobileThreadHeaderTitleSx}
                  >
                    {displayTitle}
                  </Typography>
                  <ActiveVideoJoinControl room={activeVideoRoom} onJoin={handleJoinActiveVideo} />
                </Box>

                <TeamSpacePaneTabs
                  value={activePane}
                  onChange={setActivePane}
                  t={tTasks}
                  inline
                />

                <IconButton
                  onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
                  aria-label={t("spaceSettings")}
                  size="medium"
                  sx={{ flexShrink: 0, mr: -0.5 }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
              {headerActionMenu}
            </Box>
          ) : (
            <Box
              sx={{
                px: { xs: 1.25, sm: 2 },
                pt: { xs: 1.25, sm: 1 },
                pb: { xs: 0.5, sm: 0.5 },
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.75, sm: 1.25 },
                flexShrink: 0,
              }}
            >
              {onBack ? (
                <IconButton
                  onClick={onBack}
                  aria-label={t("back")}
                  edge="start"
                  size={isCompactLayout ? "medium" : "small"}
                  sx={{ flexShrink: 0, ml: { xs: -0.75, sm: 0 }, mr: { xs: -0.25, sm: 0 } }}
                >
                  <ArrowBackIcon fontSize={isCompactLayout ? "medium" : "small"} />
                </IconButton>
              ) : null}

              <Box
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                {isEntityDiscussionSpace(space) ? (
                  <AssignmentOutlinedIcon sx={{ fontSize: { xs: 17, sm: 24 } }} />
                ) : space.isPrivate ? (
                  <LockOutlinedIcon sx={{ fontSize: { xs: 17, sm: 24 } }} />
                ) : (
                  <TagIcon sx={{ fontSize: { xs: 17, sm: 24 } }} />
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, py: isCompactLayout ? 0.125 : 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  noWrap
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" }, lineHeight: 1.35 }}
                >
                  {displayTitle}
                </Typography>
                {showSubtitle ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    display="block"
                    sx={{ lineHeight: 1.35, mt: 0.125 }}
                  >
                    {subtitleText}
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, mr: { xs: -0.75, sm: 0 }, gap: 0.5 }}>
                <ActiveVideoJoinControl room={activeVideoRoom} onJoin={handleJoinActiveVideo} />
                <TeamSpacePaneTabs
                  value={activePane}
                  onChange={setActivePane}
                  t={tTasks}
                  inline
                />
                {resolvedCardId ? (
                  <Tooltip title={t("openTaskTooltip", { id: resolvedCardId })}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AssignmentOutlinedIcon fontSize="small" />}
                      onClick={handleOpenTask}
                      sx={{ textTransform: "none", flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
                    >
                      {t("openTask")}
                    </Button>
                  </Tooltip>
                ) : null}
                {resolvedCardId ? (
                  <Tooltip title={t("openTaskTooltip", { id: resolvedCardId })}>
                    <IconButton
                      size="small"
                      onClick={handleOpenTask}
                      aria-label={t("openTask")}
                      sx={{ display: { xs: "inline-flex", sm: "none" } }}
                    >
                      <AssignmentOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {resolvedLeadId ? (
                  <Tooltip title={t("openRequestTooltip", { id: resolvedLeadId })}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ChatOutlinedIcon fontSize="small" />}
                      onClick={handleOpenRequest}
                      sx={{ textTransform: "none", flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
                    >
                      {t("openRequest")}
                    </Button>
                  </Tooltip>
                ) : null}
                {resolvedLeadId ? (
                  <Tooltip title={t("openRequestTooltip", { id: resolvedLeadId })}>
                    <IconButton
                      size="small"
                      onClick={handleOpenRequest}
                      aria-label={t("openRequest")}
                      sx={{ display: { xs: "inline-flex", sm: "none" } }}
                    >
                      <ChatOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {!standalone && space?.id ? (
                  <OpenInPopupIconButton
                    title={t("openPopupWindowTooltip")}
                    ariaLabel={t("openPopupWindow")}
                    onClick={() => openTeamPopupWindow(space.id)}
                  />
                ) : null}
                {aiSuggestButton}
                <Tooltip title={t("spaceSettings")}>
                  <IconButton
                    size="small"
                    onClick={() => setSettingsOpen(true)}
                    aria-label={t("spaceSettings")}
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {memberChips.length > 0 && !isCompactLayout ? (
            <Box
              sx={{
                px: { xs: 1.25, sm: 2 },
                pb: 0.5,
                minWidth: 0,
              }}
            >
              <TeamChannelMemberStrip
                members={memberChips}
                currentUserId={currentUserId}
                subtle
                inline
              />
            </Box>
          ) : null}
        </TeamSpacePaneChrome>
      ) : (
        <>
      {isMobileThreadHeader ? (
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: alpha(theme.palette.divider, 0.35),
            boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.06)}`,
          }}
        >
          <Box sx={teamMobileThreadHeaderRowSx}>
            <IconButton
              onClick={onBack}
              aria-label={t("back")}
              edge="start"
              size="medium"
              sx={{ flexShrink: 0, ml: -0.5 }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", pr: 0.5, gap: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={teamMobileThreadHeaderTitleSx}>
                {displayTitle}
              </Typography>
              <ActiveVideoJoinControl room={activeVideoRoom} onJoin={handleJoinActiveVideo} />
            </Box>

            <IconButton
              onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
              aria-label={t("spaceSettings")}
              size="medium"
              sx={{ flexShrink: 0, mr: -0.5 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          {headerActionMenu}
        </Box>
      ) : (
      <Box
        sx={{
          px: { xs: 1.25, sm: 2 },
          py: { xs: 0.75, sm: 1.25 },
          borderBottom: 1,
          borderTop: isCompactLayout ? 1 : 0,
          borderColor: alpha(theme.palette.divider, isCompactLayout ? 0.55 : 0.35),
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.75, sm: 1.25 },
          flexShrink: 0,
          minHeight: isCompactLayout ? 48 : undefined,
          bgcolor: "background.paper",
          boxShadow: isCompactLayout
            ? `inset 0 1px 0 ${alpha(theme.palette.divider, 0.2)}`
            : "none",
        }}
      >
        {onBack ? (
          <IconButton
            onClick={onBack}
            aria-label={t("back")}
            edge="start"
            size={isCompactLayout ? "medium" : "small"}
            sx={{ flexShrink: 0, ml: { xs: -0.75, sm: 0 }, mr: { xs: -0.25, sm: 0 } }}
          >
            <ArrowBackIcon fontSize={isCompactLayout ? "medium" : "small"} />
          </IconButton>
        ) : null}

        {isDm ? (
          isAgentDm ? (
            <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, bgcolor: "secondary.main" }}>
              <SmartToyOutlinedIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </Avatar>
          ) : isChatbotDm ? (
            <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, bgcolor: "info.main" }}>
              <SupportAgentOutlinedIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </Avatar>
          ) : dmPeerId ? (
            <TeamPresenceAvatar
              userId={dmPeerId}
              src={dmPeerAvatarUrl}
              name={title}
              size={isCompactLayout ? 32 : 40}
            />
          ) : (
            <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, bgcolor: "primary.main" }}>
              {getInitials(title)}
            </Avatar>
          )
        ) : (
          <Box
            sx={{
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            {isEntityDiscussionSpace(space) ? (
              <AssignmentOutlinedIcon sx={{ fontSize: { xs: 17, sm: 24 } }} />
            ) : space.isPrivate ? (
              <LockOutlinedIcon sx={{ fontSize: { xs: 17, sm: 24 } }} />
            ) : (
              <TagIcon sx={{ fontSize: { xs: 17, sm: 24 } }} />
            )}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{
              fontSize: { xs: "0.9375rem", sm: "1rem" },
              lineHeight: 1.25,
            }}
          >
            {displayTitle}
          </Typography>
          {showSubtitle ? (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              display="block"
              sx={{ lineHeight: 1.25, mt: 0.125 }}
            >
              {subtitleText}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            gap: 0.5,
            mr: { xs: -0.75, sm: 0 },
          }}
        >
          <ActiveVideoJoinControl room={activeVideoRoom} onJoin={handleJoinActiveVideo} />
          {resolvedCardId ? (
            <Tooltip title={t("openTaskTooltip", { id: resolvedCardId })}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AssignmentOutlinedIcon fontSize="small" />}
                onClick={handleOpenTask}
                sx={{ textTransform: "none", flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
              >
                {t("openTask")}
              </Button>
            </Tooltip>
          ) : null}

          {resolvedCardId ? (
            <Tooltip title={t("openTaskTooltip", { id: resolvedCardId })}>
              <IconButton
                size="small"
                onClick={handleOpenTask}
                aria-label={t("openTask")}
                sx={{ display: { xs: "inline-flex", sm: "none" } }}
              >
                <AssignmentOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}

          {resolvedLeadId ? (
            <Tooltip title={t("openRequestTooltip", { id: resolvedLeadId })}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ChatOutlinedIcon fontSize="small" />}
                onClick={handleOpenRequest}
                sx={{ textTransform: "none", flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
              >
                {t("openRequest")}
              </Button>
            </Tooltip>
          ) : null}

          {resolvedLeadId ? (
            <Tooltip title={t("openRequestTooltip", { id: resolvedLeadId })}>
              <IconButton
                size="small"
                onClick={handleOpenRequest}
                aria-label={t("openRequest")}
                sx={{ display: { xs: "inline-flex", sm: "none" } }}
              >
                <ChatOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}

          {!standalone && space?.id ? (
            <OpenInPopupIconButton
              title={t("openPopupWindowTooltip")}
              ariaLabel={t("openPopupWindow")}
              onClick={() => openTeamPopupWindow(space.id)}
            />
          ) : null}

          {aiSuggestButton}

          <Tooltip title={t("spaceSettings")}>
            <IconButton
              size="small"
              onClick={() => setSettingsOpen(true)}
              aria-label={t("spaceSettings")}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      )}

      {memberChips.length > 0 && !isCompactLayout ? (
        <TeamChannelMemberStrip members={memberChips} currentUserId={currentUserId} />
      ) : null}
        </>
      )}

      {activePane === "tasks" && showChannelTabs ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <BoardPage
            spaceId={Number(space.id)}
            leadId={resolvedLeadId || undefined}
            compact
            embedded
          />
        </Box>
      ) : (
      <FileDropHandler
        onFileUpload={
          dmReadOnly ? undefined : (fileOrFiles) => composerRef.current?.appendFiles(fileOrFiles)
        }
        sx={{
          flex: 1,
          minHeight: 0,
          height: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TeamMessageList
          listKey={space?.id}
          spaceId={space?.id}
          spaceLeadId={resolvedLeadId}
          spaceCardId={resolvedCardId}
          onOpenTask={handleOpenTask}
          onNavigateToSpace={onNavigateToQuotedSpace}
          messages={messages}
          currentUserId={currentUserId}
          spaceType={space?.type || "channel"}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          loadError={loadError}
          isSending={isSending}
          onReply={dmReadOnly ? undefined : handleReply}
          onEdit={dmReadOnly ? undefined : handleEdit}
          onDelete={dmReadOnly ? undefined : handleDelete}
          onForward={dmReadOnly ? undefined : handleForwardOpen}
          onReact={dmReadOnly ? undefined : handleReact}
          onAudioPlayed={handleAudioPlayed}
          onQuickReply={
            dmReadOnly || !onSend
              ? undefined
              : async (label) => {
                  await onSend({ content: label, mentions: { users: [], agents: [], chatbots: [] } });
                }
          }
        />

        <TeamTypingIndicator typingUsers={typingUsers} />

        {dmReadOnly ? (
          <Alert severity="info" sx={{ mx: { xs: 1, sm: 2 }, mb: 1, flexShrink: 0 }}>
            {t("masterDmReadOnlyHint")}
          </Alert>
        ) : (
        <TeamMessageInput
          ref={composerRef}
          onSend={onSend}
          isSending={isSending}
          leadId={resolvedLeadId}
          currentUserId={currentUserId}
          members={members}
          space={space}
          teamCompany={teamCompany}
          replyTo={replyTo}
          onClearReply={() => setReplyTo(null)}
          editingMessage={editingMessage}
          onCancelEdit={() => setEditingMessage(null)}
        />
        )}
      </FileDropHandler>
      )}

      <TeamForwardDialog
        open={forwardOpen}
        onClose={() => {
          setForwardOpen(false);
          setForwardMessage(null);
        }}
        message={forwardMessage}
        spaces={spaces}
        currentSpaceId={space?.id}
        onForward={handleForwardSubmit}
        isSending={forwardBusy}
      />

      <TeamSpaceSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        space={space}
        members={members}
        currentUserId={currentUserId}
        canManageChannel={canManageChannel}
        onUpdateSpace={onUpdateSpace}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        onDeleteSpace={onDeleteSpace}
        isBusy={isAdminBusy}
      />

      {canSuggestAiTasks ? (
        <TeamTasksFeedbackProvider>
          <AiGenerateTasksDialog
            open={aiTasksOpen}
            onClose={() => setAiTasksOpen(false)}
            spaceId={Number(space.id)}
            spaceType={space?.type || null}
            leadId={resolvedLeadId || null}
            teamCompany={teamCompany}
            messages={messages}
          />
        </TeamTasksFeedbackProvider>
      ) : null}

      <Snackbar
        open={videoPopupBlocked}
        autoHideDuration={6000}
        onClose={() => setVideoPopupBlocked(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setVideoPopupBlocked(false)} variant="filled">
          {t(
            "activeVideo.popupBlocked",
            "O navegador bloqueou a abertura da chamada. Permita pop-ups para este site e tente novamente."
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamMessagePane;
